import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { RefreshCw, SatelliteDish } from "lucide-react";
import type { Channel, ChannelResponse, CreateSessionResponse, PlaybackSession, SessionStatus } from "../shared/types";
import { filterAndPrioritizeChannels } from "./lib/channels";
import { readFavoriteChannelIds, toggleFavoriteChannelId, writeFavoriteChannelIds } from "./lib/favorites";
import { ChannelList } from "./components/ChannelList";
import { Player } from "./components/Player";
import { StatusPanel } from "./components/StatusPanel";

export function App() {
  const [channels, setChannels] = useState<Channel[]>([]);
  const [query, setQuery] = useState("");
  const [selectedChannel, setSelectedChannel] = useState<Channel | undefined>();
  const [session, setSession] = useState<PlaybackSession | undefined>();
  const [status, setStatus] = useState<SessionStatus | undefined>();
  const [loadingChannels, setLoadingChannels] = useState(true);
  const [startingSession, setStartingSession] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const [playbackMessage, setPlaybackMessage] = useState("Idle");
  const [sessionReloads, setSessionReloads] = useState(0);
  const [favoriteIds, setFavoriteIds] = useState<Set<string>>(() => readFavoriteChannelIds());
  const [favoritesOnly, setFavoritesOnly] = useState(false);
  const sessionRef = useRef<PlaybackSession | undefined>(undefined);
  const sessionRequestId = useRef(0);

  const visibleChannels = useMemo(
    () => filterAndPrioritizeChannels(channels, query, { favoriteIds, favoritesOnly }),
    [channels, favoriteIds, favoritesOnly, query]
  );
  const favoriteCount = favoriteIds.size;

  const loadChannels = useCallback(async (signal?: AbortSignal) => {
    setLoadingChannels(true);
    setError(undefined);

    try {
      const response = await fetch("/api/channels", { signal });
      if (!response.ok) {
        throw new Error(`Channel list failed: ${response.status}`);
      }

      const payload = (await response.json()) as ChannelResponse;
      setChannels(payload.channels);
      setSelectedChannel((current) => current ?? payload.channels.find((channel) => /tvri\s*sport/i.test(channel.name)));
    } catch (loadError) {
      if (loadError instanceof DOMException && loadError.name === "AbortError") {
        return;
      }
      setError(loadError instanceof Error ? loadError.message : "Failed to load channels");
    } finally {
      setLoadingChannels(false);
    }
  }, []);

  const closeSession = useCallback((sessionId: string) => {
    void fetch(`/api/sessions/${sessionId}`, { method: "DELETE" }).catch(() => undefined);
  }, []);

  const startChannel = useCallback(async (channel: Channel, signal?: AbortSignal) => {
    const requestId = sessionRequestId.current + 1;
    sessionRequestId.current = requestId;
    const previousSession = sessionRef.current;
    if (previousSession) {
      closeSession(previousSession.id);
      sessionRef.current = undefined;
    }

    setSelectedChannel(channel);
    setSession(undefined);
    setStatus(undefined);
    setStartingSession(true);
    setError(undefined);
    setPlaybackMessage("Opening stream");

    try {
      const response = await fetch("/api/sessions", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ channelId: channel.id }),
        signal
      });

      if (!response.ok) {
        throw new Error(`Session failed: ${response.status}`);
      }

      const payload = (await response.json()) as CreateSessionResponse;
      if (requestId !== sessionRequestId.current) {
        closeSession(payload.session.id);
        return;
      }

      sessionRef.current = payload.session;
      setSession(payload.session);
      setStatus(payload.status);
      setPlaybackMessage("Buffering from proxy");
    } catch (sessionError) {
      if (sessionError instanceof DOMException && sessionError.name === "AbortError") {
        return;
      }
      setError(sessionError instanceof Error ? sessionError.message : "Failed to start session");
      setPlaybackMessage("Session failed");
    } finally {
      setStartingSession(false);
    }
  }, [closeSession]);

  const restartSelectedChannel = useCallback(async () => {
    if (!selectedChannel) {
      return;
    }

    setSessionReloads((count) => count + 1);
    await startChannel(selectedChannel);
  }, [selectedChannel, startChannel]);

  const toggleFavorite = useCallback((channelId: string) => {
    setFavoriteIds((current) => {
      const next = toggleFavoriteChannelId(current, channelId);
      writeFavoriteChannelIds(next);
      return next;
    });
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    void loadChannels(controller.signal);
    return () => controller.abort();
  }, [loadChannels]);

  useEffect(() => {
    if (!session) {
      return;
    }

    sessionRef.current = session;
    const refreshStatus = async () => {
      const response = await fetch(`/api/sessions/${session.id}/status`);
      if (!response.ok) {
        return;
      }

      setStatus((await response.json()) as SessionStatus);
    };

    void refreshStatus();
    const timer = window.setInterval(() => {
      void refreshStatus();
    }, 2_000);

    return () => window.clearInterval(timer);
  }, [session]);

  useEffect(() => {
    return () => {
      const activeSession = sessionRef.current;
      if (activeSession) {
        closeSession(activeSession.id);
      }
    };
  }, [closeSession]);

  return (
    <main className="app-shell">
      <aside className="channel-rail">
        <div className="brand-row">
          <SatelliteDish aria-hidden="true" size={22} />
          <div>
            <h1>IPTV Streamer</h1>
            <span>{channels.length.toLocaleString()} channels</span>
          </div>
          <button
            className="icon-button"
            type="button"
            onClick={() => {
              void loadChannels();
            }}
            title="Reload channels"
            aria-label="Reload channels"
          >
            <RefreshCw size={17} />
          </button>
        </div>

        <ChannelList
          channels={visibleChannels}
          query={query}
          selectedId={selectedChannel?.id}
          favoriteIds={favoriteIds}
          favoriteCount={favoriteCount}
          favoritesOnly={favoritesOnly}
          loading={loadingChannels}
          onQueryChange={setQuery}
          onFavoritesOnlyChange={setFavoritesOnly}
          onToggleFavorite={toggleFavorite}
          onSelect={startChannel}
        />
      </aside>

      <section className="player-stage">
        <Player
          channel={selectedChannel}
          session={session}
          starting={startingSession}
          onFatal={restartSelectedChannel}
          onPlaybackEvent={setPlaybackMessage}
        />
        {error ? <div className="error-strip">{error}</div> : null}
      </section>

      <StatusPanel
        channel={selectedChannel}
        session={session}
        status={status}
        playbackMessage={playbackMessage}
        sessionReloads={sessionReloads}
        onRestart={restartSelectedChannel}
      />
    </main>
  );
}
